/**
 * Berichtvenster onder een collega-avatar (M4B).
 *
 * Toetsafhandeling zit bewust volledig op de `<textarea>` zelf: Cmd/Ctrl+Enter
 * verstuurt, Escape sluit. Er komt geen listener op `window` of `document` bij
 * — de bestaande sneltoetsen in Editor en Canvas filteren al op bewerkbare
 * velden (`isEditableTarget` / `inEditableField`), dus zolang de focus in dit
 * veld staat blijven Backspace, F2 en Cmd+Z van het model af zonder dat we
 * daar iets voor hoeven te veranderen. Alleen buiten-klik luistert mee, en dat
 * is muis en geen toetsenbord.
 */
import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveBilingual } from "../registry";
import { useChatStore } from "../state/chat";
import { useSession } from "../state/session";
import { useWorkspaceData } from "../state/workspace";
import { notifyChatOpened, sendChatMessage } from "./session";

export const ChatPopup = memo(function ChatPopup({
  peerEmail,
  onClose,
}: {
  peerEmail: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const myEmail = useSession((s) => s.me?.email) ?? "";
  const workspaceId = useSession((s) => s.workspaceId);
  const messages = useChatStore((s) => s.messagesByPeer[peerEmail]) ?? [];
  const activeDiagramId = useWorkspaceData((s) => s.activeDiagramId);
  // De tekst leeft in de store, niet hier: de presence-balk waar dit venster
  // in hangt hertekent bij elke verbinding die bij- of afkomt, en een
  // remount hoort je zin niet te wissen.
  const text = useChatStore((s) => s.draftByPeer[peerEmail]) ?? "";
  const setText = (next: string) => useChatStore.getState().setDraft(peerEmail, next);
  const [attachDiagram, setAttachDiagram] = useState(false);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);

  const store = useWorkspaceData.getState().store;
  const activeDiagram = activeDiagramId ? store?.getDiagram(activeDiagramId) : null;
  const activeDiagramName = activeDiagram ? resolveBilingual(activeDiagram.name, lang) : null;

  // De peer laten weten dat we kijken, zodat z'n envelopje verdwijnt.
  useEffect(() => {
    notifyChatOpened(peerEmail);
  }, [peerEmail]);

  // Nieuwste bericht in beeld houden.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, []);

  // Focus zetten, maar alleen als 'ie nog nergens anders staat, en de cursor
  // achter de bestaande tekst zetten in plaats van ervoor. `autoFocus` deed
  // dit bij élke montage opnieuw en zette de cursor dan vooraan — waardoor
  // Backspace ineens aan de verkeerde kant van je zin knabbelde.
  useEffect(() => {
    const el = taRef.current;
    if (!el || document.activeElement === el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, []);

  // Buiten-klik sluit. Muis, geen toets — de Escape zit op het invoerveld.
  //
  // In de capture-fase, want React Flow houdt de mousedown op het canvas
  // tegen voordat 'ie het document bereikt; in de bubble-fase bleef de
  // popup daardoor openstaan bij een klik op het canvas.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDown, { capture: true });
    return () => document.removeEventListener("mousedown", onDown, { capture: true });
  }, [onClose]);

  /** Nieuwe regel op de cursorpositie, met de cursor erachter. Handmatig,
   *  want het veld is controlled: laat je de browser z'n gang gaan, dan
   *  springt de cursor bij de volgende render naar het eind. */
  const insertNewline = () => {
    const el = taRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    setText(`${text.slice(0, start)}\n${text.slice(end)}`);
    const caret = start + 1;
    // Ná de render die de nieuwe waarde zet, anders overschrijft React 'm.
    requestAnimationFrame(() => {
      el.setSelectionRange(caret, caret);
    });
  };

  const send = () => {
    const body = text.trim();
    if (!body) return;
    const ref =
      attachDiagram && activeDiagramId && activeDiagramName
        ? { id: activeDiagramId, name: activeDiagramName }
        : undefined;
    sendChatMessage(peerEmail, body, ref);
    setText("");
    taRef.current?.focus();
    setAttachDiagram(false);
  };

  return (
    <div
      ref={boxRef}
      className="absolute top-8 right-0 z-50 flex w-90 flex-col gap-2 rounded border border-slate-300 bg-white p-2 text-xs shadow-lg"
      style={{ width: 360 }}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium text-slate-700">{t("chat.title", { email: peerEmail })}</span>
        <button
          type="button"
          className="rounded px-1 text-slate-400 hover:text-slate-700"
          onClick={onClose}
          aria-label={t("chat.close")}
        >
          ×
        </button>
      </div>

      <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
        {messages.length === 0 ? (
          <p className="text-slate-400">{t("chat.empty")}</p>
        ) : (
          messages.map((m) => {
            const mine = m.senderEmail === myEmail;
            return (
              <div
                key={m.id}
                className={`max-w-[85%] rounded px-2 py-1 ${
                  mine ? "self-end bg-blue-50 text-blue-900" : "self-start bg-slate-100"
                }`}
              >
                <span className="whitespace-pre-wrap break-words">{m.text}</span>
                {m.diagramRef && (
                  <button
                    type="button"
                    className="mt-1 block rounded border border-blue-300 bg-white px-1 py-0.5 text-[11px] text-blue-700"
                    onClick={() => {
                      if (!workspaceId || !m.diagramRef) return;
                      // Zonder toegang bestaat het diagram niet in onze store;
                      // dan zeggen we dat in plaats van naar niets te springen.
                      if (!useWorkspaceData.getState().store?.getDiagram(m.diagramRef.id)) {
                        window.alert(t("chat.unauthorized"));
                        return;
                      }
                      useWorkspaceData.getState().openDiagram(workspaceId, m.diagramRef.id);
                      onClose();
                    }}
                  >
                    ↗ {m.diagramRef.name}
                  </button>
                )}
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <textarea
        ref={taRef}
        rows={2}
        className="resize-none rounded border border-slate-300 px-2 py-1"
        placeholder={t("chat.placeholder")}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
            return;
          }
          if (e.key !== "Enter") return;
          // Enter verstuurt; een nieuwe regel maak je met een toets erbij.
          // Andersom dan een tekstveld normaal doet, maar dit is een
          // chatvenster: versturen is de handeling die je de hele tijd doet.
          if (e.ctrlKey || e.altKey || e.shiftKey) {
            // Zelf invoegen in plaats van de browser laten doen: alleen
            // Shift+Enter geeft van zichzelf een nieuwe regel, Ctrl+Enter en
            // Alt+Enter doen per browser iets anders (of niets).
            e.preventDefault();
            e.stopPropagation();
            insertNewline();
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          send();
        }}
      />

      <div className="flex items-center gap-2">
        {activeDiagramName && (
          <label className="flex items-center gap-1 text-[11px] text-slate-600">
            <input
              type="checkbox"
              checked={attachDiagram}
              onChange={(e) => setAttachDiagram(e.target.checked)}
            />
            {t("chat.attachDiagram", { name: activeDiagramName })}
          </label>
        )}
        <button
          type="button"
          className="ml-auto rounded bg-blue-600 px-2 py-1 text-white disabled:opacity-50"
          onClick={send}
          disabled={!text.trim()}
        >
          {t("chat.send")}
        </button>
      </div>
      <p className="text-[10px] text-slate-400">{t("chat.hint")}</p>
    </div>
  );
});
